"use client";
import {
  ChevronDown as ArrowDownIcon,
  ChevronUp as ArrowUpIcon,
} from "lucide-react";
import useTranslate from "@app/_hooks/useTranslate";

import { Button } from "@components/ngg";
import useOutsideClick from "@hooks/useOutsideClick";
import type { HeaderStoreFragment } from "@server/gql/graphql";
import { usePathname } from "next/navigation";
import { useEffect, useMemo, useState } from "react";

import { cn } from "@/lib/utils";

import Link from "@app/_components/Link";
import { Heading } from "@app/_components/ui";
import getOpeningHours from "./getOpeningHours";
import OpeningHour from "./OpeningHour";

export default function ServicePopup({
  locale,
  services,
}: {
  locale?: string;
  services: HeaderStoreFragment[];
}) {
  const t = useTranslate();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const ref = useOutsideClick(() => setDropdownOpen(false));
  const pathname = usePathname();

  useEffect(() => setDropdownOpen(false), [pathname]);

  const [openingHours, irregularHours] = useMemo(() => {
    const item = services.at(0);
    if (!item) return [[], []];
    return getOpeningHours(item);
  }, [services]);

  const service = services.at(0);

  if (!service) return null;

  return (
    <div className="relative h-full" ref={ref}>
      <Button
        onClick={() => setDropdownOpen(!dropdownOpen)}
        className={cn(
          "relative h-full gap-1 px-2",
          dropdownOpen ? "bg-white text-black" : "bg-inherit",
        )}
        endIcon={
          dropdownOpen ? (
            <ArrowUpIcon strokeWidth={1} className="size-3.5" />
          ) : (
            <ArrowDownIcon strokeWidth={1} className="size-3.5" />
          )
        }
      >
        {service.title}
      </Button>

      <div
        className={cn(
          "absolute top-full right-0 z-50 min-w-96 border-x border-b bg-white p-6",
          dropdownOpen ? "flex" : "hidden",
        )}
      >
        <div className="flex w-full flex-col items-start justify-start gap-y-6 text-sm">
          <p>{service.infoText}</p>
          <div>
            <Heading as="h6" className="all-small-caps mb-2 tracking-wider">
              {t("regularOpeningHours")}
            </Heading>
            {openingHours.map((item) => (
              <OpeningHour
                key={item.days.at(0)}
                item={item}
                closedTitle={t("closed")}
              />
            ))}
          </div>
          <div>
            <Heading as="h6" className="all-small-caps mb-2 tracking-wider">
              {t("irregularOpeningHours")}
            </Heading>
            {irregularHours.map((item) => (
              <OpeningHour
                key={item.days.at(0)}
                item={item}
                closedTitle={t("closed")}
              />
            ))}
          </div>
          <div className="flex w-full flex-col gap-y-2">
            {service.phoneNumber ? (
              <Button
                as="a"
                href={`tel:${service.phoneNumber.replace(/[-\s]/g, "")}`}
                className="btn-outlined w-full text-base"
              >
                {service.phoneNumber}
              </Button>
            ) : null}
            {service.storePage && service.storePageLinkText ? (
              <Button
                as={Link}
                locale={locale}
                href={service.storePage.slug}
                className="btn-outlined w-full text-base"
              >
                {service.storePageLinkText}
              </Button>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}
